import i18n from '@/plugins/vue-i18n';

import { OrderItem, OrderReturn } from '@/types/api/orders.types';
import { ReturnDelivery, ReturnOrderItem } from '@/types/api/returns.types';

const { t } = i18n.global;

export const structureItemsWithDeliveries = (
  items: OrderItem[],
  returns: OrderReturn[] = [],
): ReturnOrderItem[] => {
  const result: ReturnOrderItem[] = [];

  // Flatten all returned items
  const allReturnItems = returns.flatMap((r) => r.items);

  for (const item of items) {
    const itemDeliveries: ReturnDelivery[] = [];
    const valueLogs = item.valueLogs ?? [];

    // Total returned quantity for undelivered items
    const returnedQtyUndelivered = allReturnItems
      .filter((r) => r.orderItemId === item.id && r.orderDeliveryId === null)
      .reduce((sum, r) => sum + (r.quantity ?? 0), 0);

    // Delivered logs
    const deliveredLogs = valueLogs.filter((log) => log.deliveryId !== null);

    for (const log of deliveredLogs) {
      const logQty = log.quantity ?? 0;

      // Find the total returned quantity SPECIFIC to this delivery ID
      const returnedQtyForThisDelivery = allReturnItems
        .filter((r) => r.orderDeliveryId === log.deliveryId && r.orderItemId === item.id)
        .reduce((sum, r) => sum + (r.quantity ?? 0), 0);

      // The quantity to subtract is the MINIMUM of the delivered quantity and the returned quantity for THIS delivery
      const subtractQty = Math.min(logQty, returnedQtyForThisDelivery);

      // The adjusted quantity is the remaining, non-returned quantity
      const adjustedQty = Math.max(logQty - subtractQty, 0);

      if (adjustedQty > 0) {
        itemDeliveries.push({
          deliveryId: log.deliveryId,
          orderItemId: item.id,
          date: log.createdAt,
          sku: item.sku ?? '-',
          unit: item.unitType ?? '',
          quantity: adjustedQty,
          rate: log.rate ?? 0,
          returnQty: 0,
        });
      }
    }

    // pending logs: sum their quantities (only qty > 0) ---
    const pendingLogs = valueLogs.filter(
      (log) => log.deliveryId === null && (log.quantity ?? 0) > 0,
    );
    const pendingLogsSum = pendingLogs.reduce((s, l) => s + (l.quantity ?? 0), 0);

    // pendingQtyToShow = pendingLogsSum minus any returns that targeted undelivered items
    const pendingQtyToShow = Math.max(pendingLogsSum - returnedQtyUndelivered, 0);

    if (pendingQtyToShow > 0) {
      itemDeliveries.push({
        deliveryId: undefined,
        orderItemId: item.id,
        date: t('orders.issueReturn.itemTable.pendingDelivery'),
        sku: item.sku ?? '-',
        unit: item.unitType ?? '',
        quantity: pendingQtyToShow,
        rate: item.rate ?? 0,
        returnQty: 0,
      });
    }

    // Skip items with no deliveries
    if (itemDeliveries.length === 0) {
      continue;
    }

    // Sort: delivered first, pending last
    itemDeliveries.sort((a, b) => {
      const aPending = a.deliveryId === undefined;
      const bPending = b.deliveryId === undefined;
      if (aPending && !bPending) {
        return 1;
      }
      if (!aPending && bPending) {
        return -1;
      }
      return 0;
    });

    result.push({
      item,
      deliveries: itemDeliveries,
    });
  }

  return result;
};
